// GallerySection.jsx
import React, { useState, useEffect } from "react";
import "./GallerySection.css";

function ImageModal({ image, onClose, onNext, onPrev, hasNext, hasPrev }) {
  return (
    <div className="gallery-modal-overlay" onClick={onClose}>
      <div className="gallery-modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>×</button>
        
        <div className="modal-image-container">
          {hasPrev && (
            <button className="modal-nav-btn prev" onClick={onPrev}>
              ‹
            </button>
          )}
          
          <img 
            src={image} 
            alt="Gallery full view" 
            className="modal-image"
          />
          
          {hasNext && (
            <button className="modal-nav-btn next" onClick={onNext}>
              ›
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GallerySection() {
  const [imageUrls, setImageUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    fetch("https://23cvii7l7lfiexqigujv7arwee0zcdaf.lambda-url.us-east-1.on.aws/")
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        // Handle both old format (array) and new format (object with items)
        let imageList = [];

        if (Array.isArray(data)) {
          // Old format: ["url1", "url2", ...]
          imageList = data;
        } else if (data && data.items && Array.isArray(data.items)) {
          // New format: {success: true, items: [{file_name: "..."}, ...]}
          imageList = data.items.map(item =>
            `https://arely-media.s3.us-east-1.amazonaws.com/${item.file_name}`
          );
        } else {
          console.error("Unexpected data format:", data);
          return;
        }

        const images = imageList.filter((url) =>
          url.match(/\.(jpe?g|png|gif)$/i)
        );
        setImageUrls(images);
      })
      .catch((err) => {
        console.error("Fetch or network error:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleImageClick = (url, index) => {
    setSelectedImage(url);
    setSelectedIndex(index);
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
    setSelectedIndex(null);
    // Restore body scrolling
    document.body.style.overflow = 'unset';
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    if (selectedIndex > 0) {
      setSelectedImage(imageUrls[selectedIndex - 1]);
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    if (selectedIndex < imageUrls.length - 1) {
      setSelectedImage(imageUrls[selectedIndex + 1]);
      setSelectedIndex(selectedIndex + 1);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!selectedImage) return;
      
      switch(e.key) {
        case 'ArrowLeft':
          if (selectedIndex > 0) handlePrevImage(e);
          break;
        case 'ArrowRight':
          if (selectedIndex < imageUrls.length - 1) handleNextImage(e);
          break;
        case 'Escape':
          handleCloseModal();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage, selectedIndex, imageUrls.length]);

  return (
    <section id="image-gallery" className="section">
      <h2>Discover Our Collection</h2>
      <p className="gallery-description">
        Welcome to our curated gallery showcasing <strong>exceptional projects</strong> and 
        creative works. Each image represents our commitment to quality and innovation. 
        Take a moment to explore our portfolio.
      </p>

      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading images...</p>
        </div>
      )}

      {!loading && imageUrls.length === 0 && (
        <p className="no-images-message">
          No images found or failed to fetch.
        </p>
      )}

      <div className="gallery-grid">
        {imageUrls.map((url, index) => (
          <div 
            key={index} 
            className="gallery-item"
            onClick={() => handleImageClick(url, index)}
          >
            <img
              src={url}
              alt={`Gallery item ${index + 1}`}
              className="gallery-image"
              loading="lazy"
            />
            <div className="gallery-item-overlay">
              <span>Click to expand</span>
            </div>
          </div>
        ))}
      </div>

      {selectedImage && (
        <ImageModal
          image={selectedImage}
          onClose={handleCloseModal}
          onNext={handleNextImage}
          onPrev={handlePrevImage}
          hasNext={selectedIndex < imageUrls.length - 1}
          hasPrev={selectedIndex > 0}
        />
      )}
    </section>
  );
}